import { createContext, useContext, ReactNode } from 'react';
import { Auth } from 'firebase/auth';
import { Firestore } from 'firebase/firestore';
import { Functions } from 'firebase/functions';
import { FirebaseStorage } from 'firebase/storage';
import type { MesterplanApi } from '@mesterplan/api-client';

export interface CurrentUserLike {
    uid: string;
    email: string | null;
    displayName: string | null;
}

export interface FirebaseContextValue {
    auth: Auth;
    db: Firestore;
    functions: Functions;
    storage: FirebaseStorage;
    currentUser: CurrentUserLike | null;
    /**
     * Optional .NET API client. Injected by the app root from
     * `useMesterplanApi()` so that shared hooks can route Guid-keyed
     * (post-migration) entities to the .NET API while keeping a Firestore
     * fallback for legacy push-id records. `null` when the .NET clients are not
     * mounted (e.g. local Firebase-only path) — hooks must fall back to
     * Firestore in that case.
     */
    api: MesterplanApi | null;
}

const FirebaseContext = createContext<FirebaseContextValue | null>(null);

interface FirebaseProviderProps {
    children: ReactNode;
    auth: Auth;
    db: Firestore;
    functions: Functions;
    storage: FirebaseStorage;
    currentUser: CurrentUserLike | null;
    api?: MesterplanApi | null;
}

export function FirebaseProvider({
    children,
    auth,
    db,
    functions,
    storage,
    currentUser,
    api = null,
}: FirebaseProviderProps) {
    return (
        <FirebaseContext.Provider value={{ auth, db, functions, storage, currentUser, api }}>
            {children}
        </FirebaseContext.Provider>
    );
}

export function useFirebase(): FirebaseContextValue {
    const context = useContext(FirebaseContext);
    if (!context) {
        throw new Error('useFirebase must be used within a FirebaseProvider');
    }
    return context;
}

export function useFirebaseAuth(): Auth {
    return useFirebase().auth;
}

export function useFirestore(): Firestore {
    return useFirebase().db;
}

export function useFunctions(): Functions {
    return useFirebase().functions;
}

export function useStorage(): FirebaseStorage {
    return useFirebase().storage;
}

export function useCurrentUser(): CurrentUserLike | null {
    return useFirebase().currentUser;
}

/**
 * The injected .NET API client, or `null` when the .NET clients are not mounted.
 * Shared hooks use this to route Guid-keyed entities to the API.
 */
export function useNetApi(): MesterplanApi | null {
    return useFirebase().api;
}
